<template>
  <div class="mb-4 flex justify-end py-2">
    <LinkToCreate :to="{ name: 'manageCashiersCreate' }">
      Add Cashier
    </LinkToCreate>
  </div>

  <BaseCard
    class="flex h-full w-full flex-grow flex-col overflow-hidden"
  >
    <div class="flex justify-between">
      <SearchInput
        v-model="filters.search"
        class="mb-4 w-full max-w-md flex-shrink-0"
      />
    </div>
    <div class="h-full overflow-y-auto">
      <BaseTable :rows="cashiers" :columns="columns">
        <template #cell-color="{ value }">
          <ColorItem :value="value" rounded />
        </template>

        <template #cell-name="{ row }">
          <div class="w-full">
            <p class="font-semibold">
              {{ row.username }}
            </p>
          </div>
        </template>

        <template #cell-actions="{ row }">
          <div
            class="flex items-center justify-between space-x-2"
          >
            <BaseTableEditLink
              :to="{
                name: 'manageCashiersEdit',
                params: { cashierId: row.id },
              }"
            />
            <BaseTableDeleteButton
              @click="onDeleteCashier(row.id)"
            />
          </div>
        </template>

        <template #grid-card="{ row }">
          <div
            class="flex w-full items-center space-x-2 border-l-2 pl-2"
            :style="{ borderColor: row.color ?? undefined }"
          >
            <div class="flex-1">
              <p class="font-semibold">
                {{ row.username }}
              </p>
            </div>

            <ButtonWithIcon
              @click="onClickThreeDots(row.id)"
            >
              <IconHeroiconsSolidEllipsisVertical />
            </ButtonWithIcon>
          </div>
        </template>
      </BaseTable>
    </div>
  </BaseCard>

  <BaseConfirmation
    ref="deleteConfirmDialogRef"
    title="Are you sure you wish to delete this cashier?"
    confirm-label="Delete Cashier"
    cancel-label="No"
  />

  <BaseDialog
    :is-open="isActionsDialogOpen"
    @close="onActionsDialogCancel"
  >
    <ul class="flex flex-col space-y-2 p-2">
      <li class="relative cursor-default select-none">
        <button
          :class="[
            'block w-full truncate py-2 capitalize',
            'hover:bg-background-fg/3',
          ]"
          type="button"
          @click="onActionsDialogConfirm('edit')"
        >
          Edit
        </button>
      </li>

      <li class="relative cursor-default select-none">
        <button
          :class="[
            'block w-full truncate py-2 capitalize',
            'hover:bg-background-fg/3',
          ]"
          type="button"
          @click="onActionsDialogConfirm('delete')"
        >
          Delete
        </button>
      </li>
    </ul>
  </BaseDialog>
</template>

<script setup lang="ts">
import type { Cashier } from "@/services/cashiers/cashier"
import ColorItem from "@/components/ColorItem.vue"
import type { TableColumn } from "@/components/BaseTable.vue"
const filters = reactive({
  search: "",
})
const { data: cashiers } = useCashiers(filters)

const columns = ref<Array<TableColumn<Cashier>>>([
  {
    id: "color",
    header: "",
    getCellValue: (row) => row.color,
    autoWidth: true,
  },
  {
    id: "name",
    header: "Name",
    getCellValue: (row) => row.username,
  },

  {
    id: "actions",
    autoWidth: true,
  },
])

const deleteConfirmDialogRef = ref()
const onDeleteCashier = async (cashierId: string) => {
  const { isCanceled: isDeleteCancelled } =
    await deleteConfirmDialogRef.value.reveal()
  if (isDeleteCancelled) return

  try {
    await deleteCashier(cashierId)
    toast.success("Cashier deleted")
  } catch (error) {
    toast.error("Failed to delete cashier")
    logError(error)
  }
}

const {
  reveal: revealActionsDialog,
  isRevealed: isActionsDialogOpen,
  cancel: onActionsDialogCancel,
  confirm: onActionsDialogConfirm,
} = useConfirmDialog<undefined, "edit" | "delete">()

const router = useRouter()
const onClickThreeDots = async (cashierId: string) => {
  const { data, isCanceled } = await revealActionsDialog()
  if (isCanceled) return
  if (data === "edit") {
    router.push({
      name: "manageCashiersEdit",
      params: {
        cashierId: cashierId,
      },
    })
  } else if (data === "delete") {
    onDeleteCashier(cashierId)
  } else {
    return
  }
}
</script>
